import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import type { TvInterval } from '@/components/market/MarketChart';

interface ChartToolbarProps {
  interval: TvInterval;
  onIntervalChange: (i: TvInterval) => void;
  onIndicatorToggle: (indicator: string) => void;
  activeIndicators: string[];
  symbol: string;
}

const timeframes: { value: TvInterval; label: string }[] = [
  { value: '1', label: '1m' },
  { value: '5', label: '5m' },
  { value: '15', label: '15m' },
  { value: '30', label: '30m' },
  { value: '60', label: '1h' },
  { value: '240', label: '4h' },
  { value: 'D', label: '1D' },
  { value: 'W', label: '1W' },
  { value: 'M', label: '1M' },
];

const indicators = [
  { id: 'RSI', label: 'RSI (14)', study: 'RSI@tv-basicstudies' },
  { id: 'MACD', label: 'MACD', study: 'MACD@tv-basicstudies' },
  { id: 'BB', label: 'Bollinger Bands', study: 'BB@tv-basicstudies' },
  { id: 'EMA20', label: 'EMA 20', study: 'EMA@tv-basicstudies' },
  { id: 'EMA50', label: 'EMA 50', study: 'EMA@tv-basicstudies' },
  { id: 'Volume', label: 'Volume', study: 'Volume@tv-basicstudies' },
];

export default function ChartToolbar({
  interval,
  onIntervalChange,
  onIndicatorToggle,
  activeIndicators,
}: ChartToolbarProps) {
  const [indicatorsOpen, setIndicatorsOpen] = useState(false);

  return (
    <div
      className="flex items-center justify-between px-2 shrink-0 bg-background border-b border-border"
      style={{ height: 36 }}
    >
      {/* Timeframe Buttons */}
      <div className="flex items-center">
        {timeframes.map(tf => (
          <button
            key={tf.value}
            onClick={() => onIntervalChange(tf.value)}
            className={`relative px-2 text-[11px] font-medium transition-colors font-mono-data ${
              interval === tf.value ? 'text-foreground' : 'text-muted-foreground'
            }`}
            style={{ height: 36 }}
          >
            {tf.label}
            {interval === tf.value && (
              <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-accent-green"
                style={{ width: '70%', height: 2 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Indicators */}
      <div className="flex items-center gap-2">
        <Popover open={indicatorsOpen} onOpenChange={setIndicatorsOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded">
              Indicators
              {activeIndicators.length > 0 && (
                <span className="text-[9px] px-1 rounded-full bg-accent-green/20 text-accent-green">
                  {activeIndicators.length}
                </span>
              )}
              <ChevronDown className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-48 p-2 bg-popover border border-border"
            align="end"
          >
            {indicators.map(ind => (
              <label
                key={ind.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/30 cursor-pointer"
              >
                <Checkbox
                  checked={activeIndicators.includes(ind.id)}
                  onCheckedChange={() => onIndicatorToggle(ind.id)}
                  className="w-3.5 h-3.5 border-border data-[state=checked]:bg-accent-green data-[state=checked]:border-accent-green"
                />
                <span className="text-[11px] text-foreground/80">{ind.label}</span>
              </label>
            ))}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

// Export indicator map for parent to convert IDs to TradingView study strings
export const INDICATOR_STUDY_MAP: Record<string, string> = {
  RSI: 'RSI@tv-basicstudies',
  MACD: 'MACD@tv-basicstudies',
  BB: 'BB@tv-basicstudies',
  EMA20: 'EMA@tv-basicstudies',
  EMA50: 'EMA@tv-basicstudies',
  Volume: 'Volume@tv-basicstudies',
};
